import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../../../lib/cx';
import { ContextMenuItem, placeMenu, shortcutHint } from './contextMenuModel';
import styles from './BlockContextMenu.module.css';

export interface ContextMenuEntry {
  readonly id: ContextMenuItem;
  readonly label: string;
  readonly icon: ReactNode;
}

interface BlockContextMenuProps {
  readonly label: string;
  /** Items in sections; a separator is drawn between two sections. */
  readonly sections: readonly (readonly ContextMenuEntry[])[];
  /** Where the menu's top-left corner wants to sit, in viewport coordinates. */
  readonly x: number;
  readonly y: number;
  /** Run an item. The menu is closed first by the caller (2026-10b §6.3). */
  readonly onSelect: (id: ContextMenuItem) => void;
  /** Close; `restoreFocus` puts focus back on the block (Esc, Tab), not after an outside press. */
  readonly onClose: (restoreFocus: boolean) => void;
}

const isMac = (): boolean => /mac|iphone|ipad/i.test(navigator.platform);

/**
 * The block context menu (2026-10b §6.2, §6.3): a `role="menu"` panel at the pointer or the
 * block's corner, flipped at the viewport edge. Up/Down/Home/End move, Enter or Space runs,
 * Esc closes with focus back on the block. A primary press outside closes it and is consumed
 * (nothing is created or selected beneath); a secondary press is left to the lane, which
 * re-targets the menu when it lands on another block.
 */
export function BlockContextMenu({ label, sections, x, y, onSelect, onClose }: BlockContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const mac = isMac();
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  const items = (): HTMLButtonElement[] =>
    Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);

  // Position before paint (flip at the edges), then take focus on the first item.
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const { width, height } = element.getBoundingClientRect();
    const { left, top } = placeMenu(x, y, { width, height }, { width: window.innerWidth, height: window.innerHeight });
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
    element.style.visibility = 'visible';
    items()[0]?.focus({ preventScroll: true });
  }, [x, y]);

  useEffect(() => {
    const close = (restoreFocus: boolean): void => closeRef.current(restoreFocus);
    const inside = (target: EventTarget | null): boolean =>
      target instanceof Node && ref.current?.contains(target) === true;

    const onPointerDown = (event: PointerEvent): void => {
      // The secondary button is the lane's business (re-target on a block, native menu elsewhere).
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (inside(event.target)) return;
      // Consumed: nothing is created, selected or focused beneath, and the click that trails
      // this press is swallowed too (a press on a button still produces a click).
      event.stopPropagation();
      event.preventDefault();
      const swallow = (click: MouseEvent): void => {
        click.stopPropagation();
        click.preventDefault();
      };
      document.addEventListener('click', swallow, { capture: true, once: true });
      window.setTimeout(() => document.removeEventListener('click', swallow, true), 600);
      close(false);
    };
    const onContextMenu = (event: MouseEvent): void => {
      if (inside(event.target)) {
        event.preventDefault();
        return;
      }
      // On another block the lane re-targets the menu; anywhere else the native menu appears
      // and ours goes away.
      if (event.target instanceof Element && event.target.closest('[data-group-key]') !== null) return;
      close(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const list = items();
      const index = list.findIndex((item) => item === document.activeElement);
      const focusAt = (next: number): void => {
        event.preventDefault();
        list[(next + list.length) % list.length]?.focus({ preventScroll: true });
      };
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          close(true);
          return;
        case 'Tab':
          event.preventDefault();
          close(true);
          return;
        case 'ArrowDown':
          focusAt(index + 1);
          return;
        case 'ArrowUp':
          focusAt(index === -1 ? list.length - 1 : index - 1);
          return;
        case 'Home':
          focusAt(0);
          return;
        case 'End':
          focusAt(list.length - 1);
          return;
        default:
      }
    };
    const onDismissed = (): void => close(false);
    const onScroll = (event: Event): void => {
      if (!inside(event.target)) close(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('contextmenu', onContextMenu, true);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('blur', onDismissed);
    window.addEventListener('resize', onDismissed);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('contextmenu', onContextMenu, true);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('blur', onDismissed);
      window.removeEventListener('resize', onDismissed);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, []);

  return createPortal(
    <div
      ref={ref}
      className={styles.menu}
      role="menu"
      aria-label={label}
      style={{ visibility: 'hidden' }}
      data-testid="block-context-menu"
    >
      {sections.map((section, sectionIndex) => (
        <div key={section[0]?.id ?? sectionIndex} role="none">
          {sectionIndex > 0 ? <div className={styles.separator} role="separator" /> : null}
          {section.map((entry) => {
            const hint = shortcutHint(entry.id, mac);
            return (
              <button
                key={entry.id}
                type="button"
                role="menuitem"
                tabIndex={-1}
                className={cx(styles.item, entry.id === ContextMenuItem.Delete && styles.danger)}
                data-testid={`context-menu-${entry.id}`}
                onClick={() => onSelect(entry.id)}
                onMouseEnter={(event) => event.currentTarget.focus({ preventScroll: true })}
              >
                <span className={styles.icon} aria-hidden="true">
                  {entry.icon}
                </span>
                <span className={styles.label}>{entry.label}</span>
                {hint === null ? null : <span className={styles.shortcut}>{hint}</span>}
              </button>
            );
          })}
        </div>
      ))}
    </div>,
    document.body,
  );
}
